/**
 * 我的买卖 — legacy 挂卖/购买 tabs (ys142857 parity).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { otcAPI } from '../api/otc';
import { useViewAsAccountParams } from '../store/activeAccount';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedFixedTopBar } from '../components/ui/ThemedSurface';
import { MyOtcTradeCard } from '../components/trading/MyOtcTradeCard';
import { OTC_COLORS as C } from '@/constants';

type MyTab = 'sell' | 'buy';

function TabStrip({ value, onChange }: { value: MyTab; onChange: (t: MyTab) => void }) {
  const tabs: Array<{ key: MyTab; label: string }> = [
    { key: 'sell', label: '挂卖' },
    { key: 'buy', label: '购买' },
  ];

  return (
    <div className="grid grid-cols-2 gap-0 border-b" style={{ borderColor: C.line }}>
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-11 text-sm font-semibold transition-colors"
            style={{ color: active ? C.gold : C.muted }}
          >
            {active && (
              <span
                className="absolute inset-x-8 bottom-0 h-0.5 rounded-full"
                style={{ background: C.gold }}
              />
            )}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function OtcMyTrades() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<MyTab>('sell');
  const viewAsParams = useViewAsAccountParams();
  const viewAccountKey = viewAsParams?.account_id ?? 'main';

  const listQuery = useQuery({
    queryKey: ['otc', 'my-trades', tab, viewAccountKey],
    queryFn: () =>
      otcAPI.getMyTrades({ page: 1, limit: 50, side: tab, ...viewAsParams }),
    staleTime: 30_000,
  });

  const rows = listQuery.data?.rows ?? [];
  const headerTheme = {
    topbarBg: C.topbarBg,
    titleGradient: C.titleGradient,
    text: C.text,
  };

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title="我的买卖" onBack={() => navigate(-1)} theme={headerTheme} />

        <ThemedFixedTopBar background={C.topbarBg} className="border-b" style={{ borderColor: C.line }}>
          <TabStrip value={tab} onChange={setTab} />
        </ThemedFixedTopBar>

        <div className="overflow-y-auto pt-[108px] pb-6">
          {listQuery.isLoading ? (
            <div className="flex flex-col gap-2 px-2 pt-2">
              <LoadingSkeleton className="h-32" />
              <LoadingSkeleton className="h-32" />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={tab === 'sell' ? '暂无挂卖记录' : '暂无购买记录'} />
          ) : (
            rows.map((row) => (
              <MyOtcTradeCard
                key={row.id}
                row={row}
                onClick={() =>
                  navigate(`/trading/orders/${encodeURIComponent(row.php_log_id)}?side=${row.side}`, {
                    state: { row },
                  })
                }
                onPointerDown={() => {
                  void queryClient.prefetchQuery({
                    queryKey: ['otc', 'hall', 'detail', row.php_log_id, row.side],
                    queryFn: () => otcAPI.getHallDetail(row.php_log_id),
                    staleTime: 60_000,
                  });
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
